import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import environment from '@/environment';
import { withTimeout } from './live-update-startup';

/**
 * Which web bundle the native shell is serving right now.
 *
 * Read from LiveUpdate.getCurrentBundle(), i.e. the path the WebView actually
 * loads from — never the latest, downloaded or pending (`setNextBundle`) one.
 */
export type ActiveBundle = { kind: 'builtin' } | { kind: 'ota'; bundleId: string } | { kind: 'unknown' };

export type AppVersionInfo = {
  platform: string;
  isNative: boolean;
  /** Installed native app version (CFBundleShortVersionString / versionName). */
  appVersion: string | null;
  /** Installed native build number (CFBundleVersion / versionCode). */
  appBuild: string | null;
  /** Null on web, where there is no native shell or Live Update. */
  activeBundle: ActiveBundle | null;
  /** Version baked into the JS bundle that is running (built-in or OTA). */
  bundleVersion: string | null;
  environmentName: string | null;
};

export type AppVersionInfoDeps = {
  platform: string;
  isNative: boolean;
  getAppInfo: () => Promise<{ version?: string; build?: string }>;
  getCurrentBundle: () => Promise<{ bundleId: string | null }>;
  /** Deprecated pre-6.7 equivalent, for native shells with an older plugin. */
  getLegacyBundle: () => Promise<{ bundleId: string | null }>;
  bundleVersion?: string | null;
  environmentName?: string | null;
  timeoutMs?: number;
};

const NATIVE_CALL_TIMEOUT_MS = 3_000;

const nonEmpty = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

const toActiveBundle = ({ bundleId }: { bundleId: string | null }): ActiveBundle => {
  const id = nonEmpty(bundleId);
  // The plugin reports the built-in bundle (`public`) as null.
  return id ? { kind: 'ota', bundleId: id } : { kind: 'builtin' };
};

const resolveActiveBundle = async (deps: AppVersionInfoDeps, timeoutMs: number): Promise<ActiveBundle> => {
  try {
    return toActiveBundle(await withTimeout(deps.getCurrentBundle(), timeoutMs, 'getCurrentBundle'));
  } catch {
    // Fall through to the deprecated API.
  }
  try {
    return toActiveBundle(await withTimeout(deps.getLegacyBundle(), timeoutMs, 'getBundle'));
  } catch {
    return { kind: 'unknown' };
  }
};

/** Local native reads only — works offline. Never throws. */
export const resolveAppVersionInfo = async (deps: AppVersionInfoDeps): Promise<AppVersionInfo> => {
  const timeoutMs = deps.timeoutMs ?? NATIVE_CALL_TIMEOUT_MS;
  const base = {
    platform: deps.platform,
    isNative: deps.isNative,
    bundleVersion: nonEmpty(deps.bundleVersion),
    environmentName: nonEmpty(deps.environmentName),
  };
  if (!deps.isNative) {
    return { ...base, appVersion: null, appBuild: null, activeBundle: null };
  }

  const [appInfo, activeBundle] = await Promise.all([
    withTimeout(deps.getAppInfo(), timeoutMs, 'getAppInfo').catch(() => null),
    resolveActiveBundle(deps, timeoutMs),
  ]);
  return {
    ...base,
    appVersion: nonEmpty(appInfo?.version),
    appBuild: nonEmpty(appInfo?.build),
    activeBundle,
  };
};

let cached: Promise<AppVersionInfo> | null = null;

/** The active bundle only changes across a WebView load, so one read per boot is enough. */
export const getAppVersionInfo = (): Promise<AppVersionInfo> => {
  cached ??= resolveAppVersionInfo({
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
    getAppInfo: () => App.getInfo(),
    getCurrentBundle: () => LiveUpdate.getCurrentBundle(),
    getLegacyBundle: () => LiveUpdate.getBundle(),
    bundleVersion: environment.version,
    environmentName: environment.name,
  });
  return cached;
};

/** Flat shape for Datadog RUM global context (facets under `@context.app_info.*`). */
export const toRumAppInfoContext = (info: AppVersionInfo) => ({
  platform: info.platform,
  native_version: info.appVersion,
  native_build: info.appBuild,
  bundle_kind: info.activeBundle?.kind ?? 'web',
  bundle_id: info.activeBundle?.kind === 'ota' ? info.activeBundle.bundleId : null,
  bundle_version: info.bundleVersion,
  environment: info.environmentName,
});
